<script lang="ts" setup>
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionContent from 'primevue/accordioncontent'
import AccordionHeader from 'primevue/accordionheader'

interface ModifierOption {
  id: string
  name: string
  price_change: number
  is_default: boolean
}

interface _ProductModifier {
  id: string
  name: string
  is_multiple: boolean
  is_required: boolean
  number_of_options: number
  options: ModifierOption[]
  default_option: ModifierOption[]
}

interface ModifierSelection {
  id: string
  name: string
  price_modifier: number
  quantity?: number
}

interface OrderItem {
  group_id: number
  product_id: number
  product_name: string
  quantity: number
  selected_modifiers?: {
    [modifierId: string]: ModifierSelection | ModifierSelection[]
  }
}

const { comboCustomOptions } = storeToRefs(useCommerceStore())
const { t } = useI18n()

// Track which product accordion is open per combo group
const openAccordions = ref<{ [groupId: string]: string[] }>({})

// Auto-open accordions for pre-selected products when modal opens
watchEffect(() => {
  console.log('🔍 watchEffect triggered, comboCustomOptions:', comboCustomOptions.value)
  if (comboCustomOptions.value?.items && Array.isArray(comboCustomOptions.value.items)) {
    console.log('📋 Processing items:', comboCustomOptions.value.items.length)
    // Use nextTick to ensure DOM is ready
    nextTick(() => {
      comboCustomOptions.value.items.forEach((group: any, index: number) => {
        console.log(`📦 Group ${index}:`, { group_id: group.group_id, selected: group.selected })
        if (group.selected?.product_id) {
          const accordionKey = `group_${group.group_id}_product_${group.selected.product_id}`
          const productKey = `product_${group.selected.product_id}`
          console.log('🔓 Opening accordion:', { accordionKey, productKey })
          openAccordions.value[accordionKey] = [productKey]
        }
      })
      console.log('🎛️ Final openAccordions state:', openAccordions.value)
    })
  }
})

function selectProductOption(option: any, opt: any) {
  // Clear all selections for this combo group first (only one product per group)
  if (comboCustomOptions.value?.items && Array.isArray(comboCustomOptions.value.items)) {
    comboCustomOptions.value.items.forEach((group: any) => {
      if (group.group_id === option.group_id) {
        // Clear previous selection in this group
        group.selected = null
      }
    })
  }

  option.selected = {
    group_id: option.group_id,
    product_id: opt.product_id,
    product_name: opt.product_name,
    quantity: opt.quantity,
    price_variation: opt.price_variation || 0,
    selected_modifiers: {},
  }

  // Set default modifiers if product has unified modifiers
  if (opt.product_modifiers?.length > 0) {
    setDefaultModifiers(option.selected, opt.product_modifiers)
  }

  // Open this product's accordion and close others
  const accordionKey = `group_${option.group_id}_product_${opt.product_id}`
  const productKey = `product_${opt.product_id}`

  // Set only this product's accordion as open
  openAccordions.value[accordionKey] = [productKey]
}

function setDefaultModifiers(selected: OrderItem, productModifiers: any[]) {
  if (!selected.selected_modifiers) {
    selected.selected_modifiers = {}
  }

  productModifiers.forEach((modifier) => {
    if (modifier.default_option?.length > 0) {
      // Always use arrays for consistency
      selected.selected_modifiers![modifier.id] = modifier.default_option.map((opt: any) => ({
        id: opt.id,
        name: opt.name,
        price_modifier: opt.price_change || 0,
        quantity: 1,
      }))
    }
  })
}

function selectModifierOption(selected: OrderItem, modifier: any, modifierOption: any) {
  if (!selected) {
    useErrorToast('Please select an item to continue')
    return
  }

  if (!selected.selected_modifiers) {
    selected.selected_modifiers = {}
  }

  const selection: ModifierSelection = {
    id: modifierOption.id,
    name: modifierOption.name,
    price_modifier: modifierOption.price_change || 0,
    quantity: 1,
  }

  // Always use arrays for consistency
  if (!selected.selected_modifiers[modifier.id]) {
    selected.selected_modifiers[modifier.id] = []
  }
  const currentSelections = selected.selected_modifiers[modifier.id] as ModifierSelection[]

  if (modifier.is_multiple) {
    // Multiple selection modifier (like toppings) - toggle behavior
    const existingIndex = currentSelections.findIndex(s => s.id === modifierOption.id)
    if (existingIndex >= 0) {
      // Toggle off - remove selection
      currentSelections.splice(existingIndex, 1)
    }
    else {
      // Add new selection
      currentSelections.push(selection)
    }
  }
  else {
    // Single selection modifier (like size) - replace array content
    selected.selected_modifiers[modifier.id] = [selection]
  }
}

function isModifierOptionSelected(selected: OrderItem, modifier: any, modifierOption: any): boolean {
  if (!selected?.selected_modifiers?.[modifier.id]) {
    return false
  }

  const selections = selected.selected_modifiers[modifier.id] as ModifierSelection[]

  // Always expect arrays now
  return Array.isArray(selections) && selections.some(s => s.id === modifierOption.id)
}
</script>

<template>
  <div v-if="comboCustomOptions?.items">
    <Accordion
      v-for="option in comboCustomOptions.items"
      :key="option.name"
      :value="0"
      class="mb-1 rounded-lg border-2 border-b-cyan-600"
    >
      <AccordionPanel
        v-if="option?.group_name"
        class="my-1 text-lg"
        value="null"
      >
        <AccordionHeader>
          <h1 class="text-xl">
            <span>{{ option.group_name }}</span>
            <span
              v-if="option.selected"
              class="text-base"
            >
              <span> - {{ option.selected.quantity }} x {{ option.selected.product_name }} </span>
            </span>
          </h1>
        </AccordionHeader>

        <AccordionContent>
          <!-- Product Accordions -->
          <Accordion
            v-for="opt in option.options"
            :key="`${option.group_id}_${opt.product_id}`"
            v-model:value="openAccordions[`group_${option.group_id}_product_${opt.product_id}`]"
            class="mb-2 rounded-lg border border-gray-300"
          >
            <AccordionPanel
              class="product-panel"
              :value="`product_${opt.product_id}`"
            >
              <AccordionHeader>
                <div class="flex w-full items-center justify-between">
                  <!-- Product Info -->
                  <div class="flex items-center">
                    <Button
                      class="px-2 py-1 text-left mr-3"
                      :class="{
                        'bg-green-500 text-white': opt?.product_id === option?.selected?.product_id,
                        'bg-gray-200 text-gray-700': opt?.product_id !== option?.selected?.product_id,
                      }"
                      :severity="opt?.product_id === option?.selected?.product_id ? 'success' : 'secondary'"
                      @click="selectProductOption(option, opt)"
                    >
                      {{ opt?.product_id === option?.selected?.product_id ? '✓' : '+' }}
                    </Button>
                    <div>
                      <span
                        v-if="opt?.quantity"
                        class="font-semibold"
                      >{{ opt.quantity }} x </span>
                      <span
                        v-if="opt?.product_name"
                        class="font-semibold"
                      >{{ opt.product_name }}</span>
                      <span
                        v-if="opt?.price_variation"
                        class="text-black ml-2"
                      >
                        + {{ usePriceFormatter(opt.price_variation) }}
                      </span>
                    </div>
                  </div>

                  <!-- Expand indicator -->
                  <div class="text-sm text-gray-500 mr-4">
                    <span v-if="opt?.product_modifiers?.length > 0">
                      {{ t('Expand to customize') }}
                    </span>
                  </div>
                </div>
              </AccordionHeader>

              <AccordionContent>
                <!-- Product Modifiers (Unified System) -->
                <div
                  v-if="opt?.product_modifiers?.length > 0"
                  class="pl-4 pt-3"
                >
                  <template
                    v-for="modifier in opt.product_modifiers"
                    :key="modifier.id"
                  >
                    <h3
                      class="text-sm font-semibold mb-2 text-gray-800"
                    >
                      {{ modifier.name }}
                      <span
                        v-if="modifier.is_required"
                        class="text-red-500"
                      >*</span>
                      <span
                        v-if="modifier.is_multiple"
                        class="text-blue-500 text-xs ml-1"
                      >(Multiple)</span>
                    </h3>
                    <div class="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                      <template
                        v-for="modifierOption in modifier.options"
                        :key="modifierOption.id"
                      >
                        <Button
                          class="p-3 text-sm font-normal"
                          :class="{
                            'bg-orange-500 border-orange-600 text-white': isModifierOptionSelected(option?.selected, modifier, modifierOption),
                            'bg-white text-gray-700 border-gray-300': !isModifierOptionSelected(option?.selected, modifier, modifierOption),
                          }"
                          :outlined="!isModifierOptionSelected(option?.selected, modifier, modifierOption)"
                          @click="selectModifierOption(option.selected, modifier, modifierOption)"
                        >
                          {{ modifierOption.name }}
                          <span
                            v-if="modifierOption.price_change > 0"
                            class="block text-black text-xs"
                          >
                            +{{ usePriceFormatter(modifierOption.price_change) }}
                          </span>
                        </Button>
                      </template>
                    </div>
                  </template>
                </div>
              </AccordionContent>
            </AccordionPanel>
          </Accordion>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>
